import { useRef, useState, type FormEvent } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Link2, Plus, Star, X } from "lucide-react";
import { ApiError, type Product, type ProductImage, type ProductInput } from "../../lib/api";
import { parseMoney, toMajorInput } from "../../lib/money";
import { uploadProductImage, useSaveProduct } from "../../lib/store-api";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";

interface OptionDraft {
  name: string;
  values: string;
}

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/** Returns `list` with the item at `from` moved to `to`; the first photo is the main one. */
export function movePhoto<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length || to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

/** Create or edit a product: details, photos, options, stock and status. */
export function ProductEditor({
  siteId,
  currency,
  product,
  collections,
  onClose,
}: {
  siteId: string;
  currency: string;
  product: Product | null;
  collections: string[];
  onClose: () => void;
}) {
  const save = useSaveProduct(siteId);
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(product?.name ?? "");
  const [price, setPrice] = useState(toMajorInput(product?.priceMinor));
  const [compareAt, setCompareAt] = useState(toMajorInput(product?.compareAtMinor));
  const [collection, setCollection] = useState(product?.collection ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [images, setImages] = useState<ProductImage[]>(product?.images ?? []);
  const [options, setOptions] = useState<OptionDraft[]>(
    (product?.options ?? []).map((o) => ({ name: o.name, values: o.values.join(", ") })),
  );
  const [trackStock, setTrackStock] = useState(product ? product.stock !== null : true);
  const [stock, setStock] = useState(String(product?.stock ?? 10));
  const [active, setActive] = useState((product?.status ?? "active") === "active");
  const [imageLink, setImageLink] = useState("");
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const move = (from: number, to: number) => setImages((prev) => movePhoto(prev, from, to));
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (files: FileList | null) => {
    if (!files) return;
    setError(null);
    for (const file of Array.from(files)) {
      if (!PHOTO_TYPES.includes(file.type)) {
        setError("Photos must be JPEG, PNG, WebP or GIF.");
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        setError("Photos must be 5 MB or smaller.");
        continue;
      }
      setUploading(true);
      try {
        const url = await uploadProductImage(siteId, file);
        setImages((prev) => [...prev, { url, alt: name.trim() || file.name }]);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    }
  };

  const addLink = () => {
    const url = imageLink.trim();
    if (!url.startsWith("https://")) {
      setError("Photo links must start with https://");
      return;
    }
    setImages((prev) => [...prev, { url, alt: name.trim() }]);
    setImageLink("");
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const priceMinor = parseMoney(price);
    if (priceMinor === null) {
      setError("Enter a price, e.g. 25000");
      return;
    }
    const compareMinor = compareAt.trim() ? parseMoney(compareAt) : null;
    if (compareAt.trim() && compareMinor === null) {
      setError("Enter the compare-at price as a number");
      return;
    }
    const stockValue = Number.parseInt(stock, 10);
    if (trackStock && (!Number.isFinite(stockValue) || stockValue < 0)) {
      setError("Stock must be 0 or more");
      return;
    }
    const input: ProductInput = {
      name: name.trim(),
      slug: product?.slug,
      description: description.trim(),
      priceMinor,
      compareAtMinor: compareMinor,
      collection: collection.trim(),
      images: images.map((img) => ({ url: img.url, alt: img.alt || name.trim() })),
      options: options
        .filter((o) => o.name.trim())
        .map((o) => ({
          name: o.name.trim(),
          values: o.values
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),
        })),
      stock: trackStock ? stockValue : null,
      status: active ? "active" : "draft",
    };
    try {
      await save.mutateAsync({ id: product?.id, input });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the product.");
    }
  };

  return (
    <Dialog
      open
      size="lg"
      title={product ? "Edit product" : "Add product"}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="product-form" loading={save.isPending} disabled={uploading}>
            {product ? "Save changes" : "Add product"}
          </Button>
        </>
      }
    >
      <form id="product-form" className="product-form" onSubmit={submit}>
        <label className="form-field">
          <span className="form-label">Name</span>
          <input
            className="form-input"
            value={name}
            maxLength={120}
            required
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div className="form-row">
          <label className="form-field">
            <span className="form-label">Price ({currency})</span>
            <input
              className="form-input"
              inputMode="decimal"
              placeholder="25000"
              value={price}
              required
              onChange={(e) => setPrice(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-label">Compare-at price</span>
            <input
              className="form-input"
              inputMode="decimal"
              placeholder="Optional — shows a sale"
              value={compareAt}
              onChange={(e) => setCompareAt(e.target.value)}
            />
          </label>
        </div>

        <label className="form-field">
          <span className="form-label">Collection</span>
          <input
            className="form-input"
            list="product-collections"
            placeholder="e.g. Tops"
            value={collection}
            maxLength={60}
            onChange={(e) => setCollection(e.target.value)}
          />
          <datalist id="product-collections">
            {collections.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>

        <label className="form-field">
          <span className="form-label">Description</span>
          <textarea
            className="form-input"
            rows={4}
            maxLength={4000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="form-field">
          <span className="form-label">Photos</span>
          {images.length > 1 ? (
            <span className="text-muted text-sm">Drag photos to reorder. The first photo is the main one.</span>
          ) : null}
          <div className="photo-grid">
            {images.map((img, i) => (
              <div
                key={`${img.url}-${i}`}
                className="photo-grid__item"
                data-dragging={dragFrom === i ? "true" : undefined}
                data-drop-target={dragOver === i && dragFrom !== i ? "true" : undefined}
                draggable={images.length > 1}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  setDragFrom(i);
                }}
                onDragOver={(e) => {
                  if (dragFrom === null) return;
                  e.preventDefault();
                  setDragOver(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragFrom !== null) move(dragFrom, i);
                  setDragFrom(null);
                  setDragOver(null);
                }}
                onDragEnd={() => {
                  setDragFrom(null);
                  setDragOver(null);
                }}
              >
                <img src={img.url} alt={img.alt} draggable={false} />
                {i === 0 ? <span className="photo-grid__main">Main</span> : null}
                <button
                  type="button"
                  className="photo-grid__remove"
                  aria-label={`Remove photo ${i + 1}`}
                  onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                >
                  <X size={14} aria-hidden="true" />
                </button>
                {images.length > 1 ? (
                  <div className="photo-grid__tools">
                    <button
                      type="button"
                      aria-label={`Move photo ${i + 1} earlier`}
                      title="Move earlier"
                      disabled={i === 0}
                      onClick={() => move(i, i - 1)}
                    >
                      <ChevronLeft size={14} aria-hidden="true" />
                    </button>
                    {i > 0 ? (
                      <button
                        type="button"
                        aria-label={`Make photo ${i + 1} the main photo`}
                        title="Set as main photo"
                        onClick={() => move(i, 0)}
                      >
                        <Star size={13} aria-hidden="true" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      aria-label={`Move photo ${i + 1} later`}
                      title="Move later"
                      disabled={i === images.length - 1}
                      onClick={() => move(i, i + 1)}
                    >
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {images.length < 8 ? (
              <button
                type="button"
                className="photo-grid__add"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                <ImagePlus size={20} aria-hidden="true" />
                {uploading ? "Uploading…" : "Upload"}
              </button>
            ) : null}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept={PHOTO_TYPES.join(",")}
            multiple
            hidden
            onChange={(e) => {
              void upload(e.target.files);
              e.target.value = "";
            }}
          />
          <div className="link-row">
            <Link2 size={16} aria-hidden="true" />
            <input
              className="form-input"
              placeholder="…or paste an https:// image link"
              value={imageLink}
              onChange={(e) => setImageLink(e.target.value)}
            />
            <Button type="button" variant="secondary" size="sm" disabled={!imageLink.trim()} onClick={addLink}>
              Add
            </Button>
          </div>
        </div>

        <div className="form-field">
          <span className="form-label">Options</span>
          <span className="text-muted text-sm">Choices shoppers pick, like size or colour. Separate values with commas.</span>
          {options.map((opt, i) => (
            <div key={i} className="option-row">
              <input
                className="form-input"
                aria-label={`Option ${i + 1} name`}
                placeholder="Size"
                value={opt.name}
                onChange={(e) =>
                  setOptions((prev) => prev.map((o, j) => (j === i ? { ...o, name: e.target.value } : o)))
                }
              />
              <input
                className="form-input"
                aria-label={`Option ${i + 1} values`}
                placeholder="S, M, L"
                value={opt.values}
                onChange={(e) =>
                  setOptions((prev) => prev.map((o, j) => (j === i ? { ...o, values: e.target.value } : o)))
                }
              />
              <button
                type="button"
                className="icon-btn icon-btn--danger"
                aria-label={`Remove option ${i + 1}`}
                onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
          {options.length < 2 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setOptions((prev) => [...prev, { name: "", values: "" }])}
            >
              <Plus size={14} aria-hidden="true" />
              Add option
            </Button>
          ) : null}
        </div>

        <div className="form-row form-row--center">
          <label className="switch">
            <input type="checkbox" checked={trackStock} onChange={(e) => setTrackStock(e.target.checked)} />
            <span>Track stock</span>
          </label>
          {trackStock ? (
            <label className="form-field form-field--inline">
              <span className="form-label">In stock</span>
              <input
                className="form-input"
                type="number"
                min={0}
                value={stock}
                onChange={(e) => setStock(e.target.value)}
              />
            </label>
          ) : null}
        </div>

        <label className="switch">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>Visible in store</span>
        </label>

        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}
